import { LocalStorageKeys } from "@/constants/local-storage-keys";
import {
  ChatImageError,
  normaliseChatImage,
  type ChatImageData,
} from "@/features/ai-chat/utils/chat-image";
import { useToast } from "@/hooks/ui/use-toast";
import {
  Camera,
  CameraResultType,
  CameraSource,
  MediaTypeSelection,
  type MediaResult,
} from "@capacitor/camera";
import { captureException } from "@sentry/react";
import { useCallback, useState } from "react";

export type PickedPhoto = ChatImageData;

const isCameraCancel = (error: unknown): boolean => {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes("cancel");
};

const cameraErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || !("code" in error)) return undefined;
  const code = error.code;
  return typeof code === "string" || typeof code === "number" ? String(code) : undefined;
};

const isUnimplementedCameraMethod = (error: unknown): boolean => {
  if (cameraErrorCode(error) === "UNIMPLEMENTED") return true;
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.toLowerCase().includes("choosefromgallery") &&
    message.toLowerCase().includes("not implemented")
  );
};

const reportPhotoPickerFailure = (
  error: unknown,
  context: {
    source: "camera" | "gallery";
    api: "getPhoto" | "chooseFromGallery";
    fallbackAttempted: boolean;
  },
): void => {
  const exception = error instanceof Error ? error : new Error(String(error));
  const errorCode = cameraErrorCode(error);
  captureException(exception, {
    tags: {
      feature: "ai-chat",
      phase: "photo-picker",
      source: context.source,
      api: context.api,
      fallbackAttempted: String(context.fallbackAttempted),
      ...(errorCode ? { errorCode } : {}),
    },
  });
};

// Pure normalizer — shared between the picker's success path and the
// appRestoredResult path, where the camera plugin re-delivers the photo
// after an Android process-kill recovery.
export const normalizePhotoResult = async (photo: {
  base64String?: string | null;
  format?: string | null;
  path?: string | null;
  webPath?: string | null;
}): Promise<PickedPhoto | null> => {
  if (!photo.base64String) return null;
  const format = photo.format?.includes("/") ? photo.format : `image/${photo.format ?? ""}`;
  return normaliseChatImage({
    data: photo.base64String,
    mimeType: format,
    filename: photo.path ?? photo.webPath,
  });
};

const mediaMimeType = (result: MediaResult): string => {
  const format = result.metadata?.format;
  if (!format) return "";
  return format.includes("/") ? format : `image/${format}`;
};

// Camera 8 returns photo thumbnails as base64 (and returns the full image in
// this field on Web). Use that payload directly: iOS URI/webPath values point
// at temporary files and capacitor:// URLs are not reliable Fetch resources
// when the WebView is loading the remotely hosted app.
export const mediaResultToPicked = async (result: MediaResult): Promise<PickedPhoto | null> => {
  const data = result.thumbnail;
  if (!data) return null;

  return normaliseChatImage({
    data,
    mimeType: mediaMimeType(result),
    filename: result.uri ?? result.webPath,
  });
};

export const usePhotoPicker = () => {
  const { showError } = useToast();
  const [isOpening, setIsOpening] = useState(false);

  const pickFromSource = useCallback(
    async (source: CameraSource): Promise<PickedPhoto | null> => {
      if (isOpening) return null;
      setIsOpening(true);
      // Mark this camera launch as chat-initiated. If Android kills our
      // process while the camera Activity is foregrounded, the
      // appRestoredResult listener uses this flag to route the restored
      // photo back into the chat composer.
      localStorage.setItem(LocalStorageKeys.CHAT_CAMERA_IN_PROGRESS_KEY, "1");

      try {
        if (source === CameraSource.Camera) {
          const result = await Camera.requestPermissions({ permissions: ["camera"] });
          if (result.camera !== "granted") {
            showError("Please allow camera access in Settings to continue.");
            return null;
          }
        }

        const photo = await Camera.getPhoto({
          quality: 85,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source,
        });

        const picked = await normalizePhotoResult(photo);
        if (!picked) {
          showError("Unable to access the selected photo.");
          return null;
        }
        return picked;
      } catch (error) {
        if (isCameraCancel(error)) return null;
        reportPhotoPickerFailure(error, {
          source: "camera",
          api: "getPhoto",
          fallbackAttempted: false,
        });
        if (error instanceof ChatImageError) {
          showError(error.message);
          return null;
        }
        showError("Couldn't access photos. Please check permissions and try again.");
        return null;
      } finally {
        setIsOpening(false);
        localStorage.removeItem(LocalStorageKeys.CHAT_CAMERA_IN_PROGRESS_KEY);
      }
    },
    [isOpening, showError],
  );

  const takePhoto = useCallback(() => pickFromSource(CameraSource.Camera), [pickFromSource]);

  const pickFromGallery = useCallback(
    async (limit: number): Promise<PickedPhoto[]> => {
      if (isOpening || limit <= 0) return [];
      setIsOpening(true);

      try {
        let results: MediaResult[];
        try {
          ({ results } = await Camera.chooseFromGallery({
            mediaType: MediaTypeSelection.Photo,
            allowMultipleSelection: true,
            limit,
            includeMetadata: true,
            quality: 85,
            targetWidth: 2048,
            targetHeight: 2048,
            correctOrientation: true,
          }));
        } catch (error) {
          if (!isUnimplementedCameraMethod(error)) throw error;

          try {
            const photo = await Camera.getPhoto({
              quality: 85,
              allowEditing: false,
              resultType: CameraResultType.Base64,
              source: CameraSource.Photos,
            });
            const picked = await normalizePhotoResult(photo);
            if (!picked) {
              reportPhotoPickerFailure(new Error("Legacy gallery picker returned no image data"), {
                source: "gallery",
                api: "getPhoto",
                fallbackAttempted: true,
              });
              showError("Unable to access the selected photo.");
              return [];
            }
            return [picked];
          } catch (fallbackError) {
            if (isCameraCancel(fallbackError)) return [];
            reportPhotoPickerFailure(fallbackError, {
              source: "gallery",
              api: "getPhoto",
              fallbackAttempted: true,
            });
            if (fallbackError instanceof ChatImageError) {
              showError(fallbackError.message);
            } else {
              showError("Couldn't access photos. Please check permissions and try again.");
            }
            return [];
          }
        }

        const picked: PickedPhoto[] = [];
        let firstErrorMessage: string | null = null;
        for (const result of results) {
          try {
            const photo = await mediaResultToPicked(result);
            if (photo) {
              picked.push(photo);
            } else {
              firstErrorMessage ??= "Unable to access one of the selected photos.";
            }
          } catch (error) {
            if (error instanceof ChatImageError) {
              firstErrorMessage ??= error.message;
            } else {
              reportPhotoPickerFailure(error, {
                source: "gallery",
                api: "chooseFromGallery",
                fallbackAttempted: false,
              });
              firstErrorMessage ??= "Unable to access one of the selected photos.";
            }
          }
        }

        if (firstErrorMessage) showError(firstErrorMessage);
        return picked;
      } catch (error) {
        if (isCameraCancel(error)) return [];
        reportPhotoPickerFailure(error, {
          source: "gallery",
          api: "chooseFromGallery",
          fallbackAttempted: false,
        });
        showError("Couldn't access photos. Please check permissions and try again.");
        return [];
      } finally {
        setIsOpening(false);
      }
    },
    [isOpening, showError],
  );

  return { takePhoto, pickFromGallery, isOpening };
};
